import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('您的消息已发送成功，我们会尽快回复。');
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q8546e3oq4jqumthqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('您的消息已发送成功，我们会尽快回复。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormMessage(serverMsg || '发送失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络连接后重试。');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 安全指南',
    description: '安全指南致力于提供成人网站安全访问、隐私保护与内容分级的专业指导。',
    url: 'https://safe-guide.example.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '安全指南',
      url: 'https://safe-guide.example.com/',
    },
    about: {
      '@type': 'Organization',
      name: '安全指南',
      description: '专业的网络安全教育与内容分级指导平台',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <main className="min-h-screen pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
              关于我们
            </h1>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-4 font-heading">
                  品牌介绍
                </h2>
                <div className="prose-custom text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
                  <p>
                    <strong>安全指南</strong>是一个专注于网络隐私保护与内容安全的专业教育平台。我们的使命是帮助成年互联网用户安全、理性地了解和管理在线内容，避免网络安全风险与法律问题。
                  </p>
                  <p>
                    自成立以来，我们始终秉持"理性、安全、负责任"的核心理念，为用户提供科学、客观的网络安全知识。我们的内容团队由网络安全专家、法律顾问和心理咨询师组成，确保每一篇文章都经过专业审核。
                  </p>
                  <p>
                    我们不鼓励也不推广任何形式的违规行为，而是致力于帮助用户在合法合规的框架下，建立健康的网络使用习惯和隐私保护意识。
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-4 font-heading">
                  我们的信誉
                </h2>
                <div className="space-y-3">
                  {[
                    { icon: 'ri-award-line', title: '专业认证', desc: '团队成员持有CISSP、CIPP/E等国际安全与隐私认证' },
                    { icon: 'ri-team-line', title: '专家团队', desc: '由网络安全专家、法律顾问和心理咨询师组成的编辑团队' },
                    { icon: 'ri-article-line', title: '严格审核', desc: '每篇文章经过双重事实核查和专业审核流程' },
                    { icon: 'ri-user-heart-line', title: '用户至上', desc: '以保护用户隐私和安全为首要原则，绝不追踪用户行为' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70">
                      <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-primary-100/80 text-primary-700">
                        <i className={`${item.icon} text-lg`}></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900 mb-0.5 font-heading">{item.title}</h4>
                        <p className="text-xs md:text-sm text-foreground-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-2 font-heading">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600">
                如有任何问题或建议，欢迎通过以下表单与我们取得联系
              </p>
            </div>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8 space-y-5"
            >
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  姓名 <span className="text-accent-600">*</span>
                </label>
                <input
                  type="text"
                  id="contact-name"
                  name="name"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  placeholder="请输入您的姓名"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  邮箱 <span className="text-accent-600">*</span>
                </label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  placeholder="请输入您的邮箱地址"
                />
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  placeholder="请输入消息主题"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  消息 <span className="text-accent-600">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                  placeholder="请输入您的消息（不超过500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              <div className="honeypot-field">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-2.5 rounded-md font-medium text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-send-plane-line"></i>
                发送消息
              </button>

              {formStatus === 'success' && (
                <div className="p-3 rounded-md bg-primary-100/80 text-primary-700 text-sm flex items-center gap-2">
                  <i className="ri-checkbox-circle-line"></i>
                  {formMessage}
                </div>
              )}
              {formStatus === 'error' && (
                <div className="p-3 rounded-md bg-accent-100/80 text-accent-700 text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>
                  {formMessage}
                </div>
              )}
            </form>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: 'ri-mail-line', label: '邮箱', value: 'support@safe-guide.example.com' },
                { icon: 'ri-time-line', label: '工作时间', value: '周一至周五 9:00-18:00' },
                { icon: 'ri-map-pin-line', label: '地址', value: '中国 · 北京' },
              ].map((item) => (
                <div key={item.label} className="text-center p-4 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary-100/80 text-primary-700">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-0.5">{item.label}</p>
                  <p className="text-sm font-medium text-foreground-800">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="privacy" className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 font-heading">
              隐私政策
            </h2>
            <div className="prose-custom text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
              <p>
                安全指南高度重视用户隐私保护。我们承诺：本网站不收集任何用户的个人身份信息，不使用追踪Cookie，不记录用户的浏览行为。
              </p>
              <p>
                联系我们表单所收集的信息（姓名、邮箱、消息内容）仅用于回复用户的咨询，不会用于任何其他目的，不会出售或分享给任何第三方。
              </p>
              <p>
                本网站使用HTTPS加密传输，确保所有数据在传输过程中得到保护。我们定期审查和更新安全措施，以维护用户数据的安全性。
              </p>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}